/**
 * roleContractEditorState — shared state types, helpers, and constants for the
 * RoleContractEditor and its 8 section sub-components.
 *
 * Extracted in BD-3 (A.1 + A.2) to:
 *  - Eliminate `as any` casts in hydrate() by using narrower DTO types.
 *  - Allow section components to import state without circular deps.
 */

import { t } from '@/lib/i18n';
import type { RorosRoleContractDTO } from '@/services/roros/governance.service';

// ─── Typed sub-shapes (A.2) ───────────────────────────────────────────────────

export interface ToolAccessPolicyShape {
  grants?: Array<{ tool: string; accessLevel: string }>;
  budget_cap_usd?: number | null;
}

export interface DecisionRightsShape {
  root?: string;
  trunk?: string;
  branch?: string;
  leaf?: string;
}

// ─── Editor state ─────────────────────────────────────────────────────────────

export type EditorState = {
  name: string;
  actorId: string;
  purposeScope: string;
  outcomesOwnership: Array<{ metricId: string; metricName?: string; access: 'read' | 'write' }>;
  /**
   * Entries with a routineId are normal bindings rendered as chips.
   * Legacy category entries (no routineId) are passed through to the API unchanged.
   * The index signature accommodates those extra keys without narrowing issues.
   */
  routineOwnership: Array<{ routineId?: string; routineName?: string | null; archived?: boolean; authority?: string; [key: string]: unknown }>;
  decisionRights: DecisionRightsShape;
  toolBudgetCap: string;
  toolGrants: Array<{ tool: string; accessLevel: string }>;
  qcCanPerform: boolean;
  qcTypes: string[];
  qcChecklist: string;
  reportingTo: string[];
  collaboratesWith: string[];
  escalatesTo: string[];
  upgradeAutoSuggest: string[];
  upgradeRequiresCeo: string[];
  upgradeMaxPerMonth: string;
};

// ─── Constants ────────────────────────────────────────────────────────────────

export const NOT_SET = '__none__';

export const DECISION_ACTIONS = [
  { value: NOT_SET, label: t('— not set —') },
  { value: 'autonomous', label: t('Autonomous') },
  { value: 'requires_approval', label: t('Requires approval') },
  { value: 'must_escalate', label: t('Must escalate') },
];

export const QC_TYPE_OPTIONS = [
  { value: 'self', label: t('Self-check') },
  { value: 'peer', label: t('Peer review') },
  { value: 'qc_role', label: t('Dedicated QC role') },
];

export const COMPONENT_TABS = [
  { value: 'purpose', label: t('Purpose & Scope') },
  { value: 'outcomes', label: t('Outcomes') },
  { value: 'routines', label: t('Routines') },
  { value: 'decisions', label: t('Decisions') },
  { value: 'tools', label: t('Tools & Visibility') },
  { value: 'qc', label: t('QC Duties') },
  // Sven dogfood 2026-05-03: "Interfaces" was opaque — the tab is
  // about who the role reports to, collaborates with, and escalates
  // to. Renamed for clarity. The persisted JSONB key stays
  // `interfaces` to avoid a migration.
  { value: 'interfaces', label: t('Reports & Collaboration') },
  { value: 'upgrades', label: t('Upgrades') },
];

export const COMPONENT_KEYS = [
  'purpose_scope',
  'outcomes_ownership',
  'routine_ownership',
  'decision_rights',
  'tool_access_policy',
  'qc_duties',
  'interfaces',
  'upgrade_rules',
];

/**
 * Sven dogfood 2026-05-03: human-readable labels for the progress
 * chips at the top of the editor. Pre-fix the chips showed raw enum
 * keys (`purpose_scope`, `outcomes_ownership`, etc.) which were
 * unreadable for non-developers.
 */
export const COMPONENT_LABELS: Record<string, string> = {
  purpose_scope: t('Purpose & Scope'),
  outcomes_ownership: t('Outcomes'),
  routine_ownership: t('Routines'),
  decision_rights: t('Decisions'),
  tool_access_policy: t('Tools & Visibility'),
  qc_duties: t('QC Duties'),
  interfaces: t('Reports & Collaboration'),
  upgrade_rules: t('Upgrades'),
};

// ─── State factory ────────────────────────────────────────────────────────────

export function emptyState(): EditorState {
  return {
    name: '',
    actorId: '',
    purposeScope: '',
    outcomesOwnership: [],
    routineOwnership: [],
    decisionRights: {},
    toolBudgetCap: '',
    toolGrants: [],
    qcCanPerform: false,
    qcTypes: [],
    qcChecklist: '',
    reportingTo: [],
    collaboratesWith: [],
    escalatesTo: [],
    upgradeAutoSuggest: [],
    upgradeRequiresCeo: [],
    upgradeMaxPerMonth: '',
  };
}

// ─── Hydration (DTO → EditorState) ───────────────────────────────────────────
// A.2: narrowed DTO types remove all `as any` casts that existed in the
// original hydrate() function.

export function hydrate(c: RorosRoleContractDTO): EditorState {
  // toolAccessPolicy: the DTO allows Record<string, unknown> | unknown[].
  // We cast to ToolAccessPolicyShape after runtime checks.
  const tapRaw = c.toolAccessPolicy;
  const tap: ToolAccessPolicyShape = (Array.isArray(tapRaw) || tapRaw === null || tapRaw === undefined)
    ? {}
    : (tapRaw as ToolAccessPolicyShape);

  const grants: Array<{ tool: string; accessLevel: string }> = Array.isArray(tap.grants)
    ? tap.grants
    : Array.isArray(tapRaw)
      ? (tapRaw as Array<{ tool: string; accessLevel: string }>)
      : [];
  const cap = tap.budget_cap_usd;

  const qc = (c.qcDuties ?? {}) as { canPerformQc?: boolean; qcTypes?: string[]; qualityChecklist?: string[] | string };
  const ifaces = (c.interfaces ?? {}) as { reportingTo?: string[]; collaboratesWith?: string[]; escalatesTo?: string[] };
  const up = (c.upgradeRules ?? {}) as { autoSuggestComponents?: string[]; requiresCeoReview?: string[]; maxAutoUpgradesPerMonth?: number };

  // decisionRights: DTO is Record<string, string> — safe to treat as DecisionRightsShape
  const dr = (c.decisionRights ?? {}) as DecisionRightsShape;

  return {
    name: c.name ?? '',
    actorId: c.actorId ?? '',
    purposeScope: c.purposeScope ?? '',
    outcomesOwnership: c.outcomesOwnership ?? [],
    routineOwnership: c.routineOwnership ?? [],
    decisionRights: {
      root: dr.root ?? '',
      trunk: dr.trunk ?? '',
      branch: dr.branch ?? '',
      leaf: dr.leaf ?? '',
    },
    toolBudgetCap: cap === null || cap === undefined ? '' : String(cap),
    toolGrants: grants,
    qcCanPerform: Boolean(qc.canPerformQc),
    qcTypes: Array.isArray(qc.qcTypes) ? qc.qcTypes : [],
    qcChecklist: Array.isArray(qc.qualityChecklist)
      ? qc.qualityChecklist.join('\n')
      : typeof qc.qualityChecklist === 'string'
        ? qc.qualityChecklist
        : '',
    reportingTo: ifaces.reportingTo ?? [],
    collaboratesWith: ifaces.collaboratesWith ?? [],
    escalatesTo: ifaces.escalatesTo ?? [],
    upgradeAutoSuggest: up.autoSuggestComponents ?? [],
    upgradeRequiresCeo: up.requiresCeoReview ?? [],
    upgradeMaxPerMonth: up.maxAutoUpgradesPerMonth !== undefined ? String(up.maxAutoUpgradesPerMonth) : '',
  };
}

// ─── Serialisation (EditorState → API payload) ───────────────────────────────

export function toPayload(s: EditorState): Record<string, unknown> {
  const checklist = s.qcChecklist
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  const cap = s.toolBudgetCap.trim() === '' ? null : Number(s.toolBudgetCap);
  return {
    actor_id: s.actorId || null,
    name: s.name.trim(),
    purpose_scope: s.purposeScope,
    outcomes_ownership: s.outcomesOwnership,
    // Strip routineName + archived flag — canonical write sends only routineId + authority.
    // The server also strips these on save; this is belt-and-suspenders.
    // Legacy category entries (no routineId) are passed through unchanged so no data is lost.
    routine_ownership: s.routineOwnership.map((entry) => {
      if (typeof entry.routineId !== 'string') return entry; // legacy — pass through
      const { routineId, authority } = entry as { routineId: string; authority?: string };
      return authority ? { routineId, authority } : { routineId };
    }),
    decision_rights: Object.fromEntries(
      Object.entries(s.decisionRights).filter(([, v]) => v && v !== ''),
    ),
    tool_access_policy: {
      grants: s.toolGrants,
      budget_cap_usd: Number.isFinite(cap as number) && (cap as number) > 0 ? cap : null,
    },
    qc_duties: {
      canPerformQc: s.qcCanPerform,
      qcTypes: s.qcTypes,
      qualityChecklist: checklist,
    },
    interfaces: {
      reportingTo: s.reportingTo,
      collaboratesWith: s.collaboratesWith,
      escalatesTo: s.escalatesTo,
    },
    upgrade_rules: {
      autoSuggestComponents: s.upgradeAutoSuggest,
      requiresCeoReview: s.upgradeRequiresCeo,
      maxAutoUpgradesPerMonth: s.upgradeMaxPerMonth.trim() === '' ? undefined : Number(s.upgradeMaxPerMonth),
    },
  };
}
